import { Roles } from 'consts/authConsts'
import { TagTypeToQueryType } from 'consts/filterConsts'
import { isEmpty } from 'lodash'
import { isUserOfRole, isUserOfAnyAdminRole, getParsedIdToken } from 'utils/authUtils'

export const createPatientsListFilter = ({ isAdmin, doctorId, filter, allowedGroups, hiTrackedOnlyFilter }) => {
  let queryFilter = {}
  const FILTER_OPERATOR = 'eq'

  let initialFilter = {
    ...(isUserOfRole([Roles.Admin, Roles.HIAdmin]) || !allowedGroups?.length
      ? {}
      : buildReadersFilter(allowedGroups, 'PatientSearchModel')),
    ...(isUserOfAnyAdminRole()
      ? {
          program: { ne: 'whitening' }
        }
      : {
          doctorId: { eq: doctorId },
          statusType: { ne: 'archived' }
        })
  }

  if (filter?.tagsFilter || !!filter?.searchFilter) {
    queryFilter = buildPatientsFilter(
      { filterData: filter?.tagsFilter, operator: FILTER_OPERATOR },
      'and',
      filter?.searchFilter
    )
  }

  if (filter?.tagsFilter?.status?.includes('archived')) {
    delete initialFilter.statusType
  }

  let result = { ...initialFilter, ...queryFilter }

  if (isUserOfAnyAdminRole() && filter?.doctorFilter?.length) {
    result.and = [
      ...(result.and || []),
      {
        or: filter.doctorFilter.map(doctorId => ({
          doctorId: { eq: doctorId }
        }))
      }
    ]
  }

  if (hiTrackedOnlyFilter) {
    result.and = [...(result.and || []), hiTrackedOnlyFilter]
  }

  return result
}

const isAllowedForMultiGroups = allowedGroups => allowedGroups.length > 0

// Max length of a free-text patient search term. Anything longer is truncated before it
// reaches OpenSearch, so a pathological input (e.g. a giant pasted string) can't overload
// the shared search cluster. See the 2026-07-15 patient-search RCA.
export const MAX_SEARCH_LEN = 100

export const buildPatientsFilter = (filter, operation = 'and', searchFilter) => {
  let queryFilter = {}
  queryFilter[operation] = []

  if (filter.filterData) {
    const { filterData, operator } = filter

    Object.keys(filterData).forEach(filterType => {
      if (filterData[filterType].length > 0) {
        let typeFilter = { or: [] }
        const newTagType = TagTypeToQueryType[filterType]

        filterData[filterType].forEach(filterValue => {
          if (filterType === 'assignee' && filterValue === 'Unassigned') {
            // Check whether isAssigned is either null or false
            typeFilter['or'].push({ and: [{ isAssigned: { ne: false } }, { isAssigned: { ne: true } }] })
            typeFilter['or'].push({ isAssigned: { eq: false } })
          } else if (filterType === 'chat' && filterValue === 'unread') {
            typeFilter['or'].push({ isChatRoomResolved: { eq: false } })
          } else if (filterType === 'scan' && filterValue === 'pendingReview') {
            typeFilter['or'].push({ trackingStatus: { eq: 'pending' } })
          } else {
            typeFilter['or'].push({ [newTagType]: { [operator]: filterValue } })
          }
        })
        queryFilter[operation].push(typeFilter)
      }
    })
  }

  // Bound + sanitize the free-text term before it becomes an OpenSearch query. A leading
  // wildcard (`*term*`) can't use the index — it scans every name term — and with an
  // unbounded term it can pin the shared cluster (2026-07-15 RCA). Cap the length and match
  // each word as an index-friendly prefix instead of a leading wildcard.
  const term = (searchFilter || '').trim().slice(0, MAX_SEARCH_LEN)

  if (term) {
    if (!queryFilter['or']) {
      queryFilter['or'] = []
    }

    const searchKeywords = term.split(/\s+/).filter(Boolean)

    queryFilter['or'] = [
      ...queryFilter['or'],
      {
        // Match each typed word as a prefix (order-independent, uses the index).
        and: searchKeywords.map(word => ({
          name: { matchPhrasePrefix: word }
        }))
      },
      {
        name: { matchPhrasePrefix: term }
      },
      {
        email: { matchPhrasePrefix: term }
      },
      {
        communicationEmails: { matchPhrasePrefix: term }
      },
      {
        id: { eq: term }
      }
    ]
  }

  if (isEmpty(queryFilter[operation]) && (!queryFilter['or'] || queryFilter['or']?.length === 0)) {
    return {}
  }

  return queryFilter
}

export const buildReadersFilter = (allowedGroups = [], entityType, operator = 'eq') => ({
  or: allowedGroups.map(group => ({
    a_readers: {
      [operator]: `${entityType}_${group.groupKey}_R`
    }
  }))
})

export const buildHiTrackedOnlyStatusFilter = statusValues => {
  if (!statusValues?.length) return null
  return { or: statusValues.map(s => ({ statusType: { eq: s } })) }
}

// Sentinel doctorId used while HI eligibility data is still loading. No real
// PatientSearchModel.doctorId can equal this value, so OpenSearch returns zero
// rows — keeping the list empty until ffOn/ffOff are populated, instead of
// degrading to "no restriction" and leaking un-tracked patients.
export const HI_TRACKED_LOADING_SENTINEL = '__hi_tracked_loading__'

export const buildHiTrackedOnlyFilter = (ffOnDoctorIds, ffOffDoctorIds) => {
  const clauses = []

  if (ffOnDoctorIds.length) {
    clauses.push({
      and: [
        { or: ffOnDoctorIds.map(id => ({ doctorId: { eq: id } })) },
        {
          or: [
            { statusType: { eq: 'active-treatment' } },
            { statusType: { eq: 'observation' } },
            { statusType: { eq: 'retention' } }
          ]
        }
      ]
    })
  }

  if (ffOffDoctorIds.length) {
    clauses.push({
      and: [{ or: ffOffDoctorIds.map(id => ({ doctorId: { eq: id } })) }, { statusType: { eq: 'active-treatment' } }]
    })
  }

  if (!clauses.length) {
    // Both lists empty = eligibility data hasn't loaded yet (race on initial
    // render when the toggle is restored from URL ?hiTrackedOnly=true before
    // assignedDoctors arrives). Returning null here would drop the HI
    // restriction from createPatientsListFilter and the user would briefly see
    // every patient they have read auth for. Return a sentinel that matches
    // nothing instead; effect #2 in PatientsFilter will rebuild this filter
    // with real doctor IDs once eligibility data lands.
    return { doctorId: { eq: HI_TRACKED_LOADING_SENTINEL } }
  }

  return { or: clauses }
}

export const and = (expressions = []) => {
  const nonEmptyExpressions = expressions.filter(exp => !isEmpty(exp))
  return isEmpty(nonEmptyExpressions)
    ? {}
    : {
        and: nonEmptyExpressions
      }
}

/**
 * The clause that limits a patient search to what the current user may read, or `null` for an
 * admin who may read everything.
 *
 * Exported so a caller can AND it over a whole filter. `buildPatientSearchFilter` below spreads it
 * into its `name` and `email` branches only — its `id` and `username` branches are deliberately
 * unscoped — which is safe for a term the user typed but not for one taken from an untrusted
 * source, where an exact id could surface another practice's patient.
 */
export const buildPatientScopeFilter = (doctorUsername, allowedGroups = []) => {
  if (isUserOfRole([Roles.Admin, Roles.DevAdmin])) {
    return null
  }

  if (isAllowedForMultiGroups(allowedGroups)) {
    return buildReadersFilter(allowedGroups, 'PatientSearchModel')
  }

  return { doctorUsername: { eq: doctorUsername } }
}

export const buildPatientSearchFilter = (filter, doctorUsername, allowedGroups) => {
  const extraFilter = buildPatientScopeFilter(doctorUsername, allowedGroups) || {}

  return {
    or: [
      {
        name: { matchPhrasePrefix: filter },
        ...extraFilter
      },
      {
        email: { matchPhrasePrefix: filter },
        ...extraFilter
      },
      {
        // `email` is the account email, which is an Apple private relay address for
        // Sign-in-with-Apple signups. The address the practice corresponds with lives
        // in communicationEmails, so it has to be searchable too (buildPatientsFilter
        // and the support dashboard search already cover it).
        communicationEmails: { matchPhrasePrefix: filter },
        ...extraFilter
      },
      {
        id: { eq: filter }
      },
      {
        username: { eq: filter }
      }
    ]
  }
}

export const buildDoctorsSearchFilter = ({ filterTerm, groups, doctorIds = [], includeMembers = true }) => {
  let filterBase = includeMembers
    ? {}
    : {
        and: [
          !includeMembers
            ? {
                accessType: { eq: 'owner' },
                roles: {
                  eq: Roles.AccountOwners
                }
              }
            : {}
        ]
      }

  if (filterTerm) {
    filterBase = {
      ...filterBase,
      or: [
        {
          name: {
            matchPhrasePrefix: filterTerm
          }
        },
        {
          email: {
            matchPhrasePrefix: filterTerm
          }
        },
        {
          practiceName: {
            matchPhrasePrefix: filterTerm
          }
        },
        {
          id: { eq: filterTerm }
        },
        {
          username: { eq: filterTerm }
        }
      ]
    }
  }

  if (!groups) {
    return filterBase
  }

  let hiAdminFilter = {}

  if (isUserOfRole([Roles.HIAdmin])) {
    const hiGroup = JSON.parse(getParsedIdToken()?.extraData || '{}')?.hiGroup
    if (hiGroup) {
      hiAdminFilter = {
        hiGroup: {
          eq: hiGroup
        }
      }
    }
  }

  const finalFilter = and([
    filterBase,
    !isEmpty(hiAdminFilter)
      ? hiAdminFilter
      : {
          or: groups.map(group => ({
            a_readers: {
              eq: `DoctorSearchModel_${group}_R`
            }
          }))
        }
  ])

  if (doctorIds?.length) {
    finalFilter.and.push({
      or: doctorIds.map(doctorId => ({
        doctorId: { eq: doctorId }
      }))
    })
  }

  return finalFilter
}
